import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { label: "Solutions", hash: "#solutions", testid: "navbar-link-solutions" },
  { label: "Livrables", hash: "#solutions", testid: "navbar-link-livrables" },
  { label: "Méthodologie", hash: "#triple-culture", testid: "navbar-link-methodologie" },
  { label: "Pourquoi ACS", hash: "#pourquoi-acs", testid: "navbar-link-pourquoi-acs" },
];

export const scrollToHash = (hash) => (e) => {
  e.preventDefault();
  const el = document.querySelector(hash);
  if (!el) {
    window.location.href = `/${hash}`;
    return;
  }
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const toggle = (value) => {
    setOpen(value);
    if (window.__lenis) value ? window.__lenis.stop() : window.__lenis.start();
  };

  const go = (hash) => (e) => {
    toggle(false);
    scrollToHash(hash)(e);
  };

  return (
    <>
      <motion.header
        initial={{ y: -72, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800"
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 h-[72px] flex items-center justify-between">
          <a
            href="#hero"
            onClick={scrollToHash("#hero")}
            data-testid="navbar-logo"
            className="flex items-center"
          >
            <img src="/logo-acs-noir.webp" alt="ACS Corporate" className="h-8 w-auto brightness-0 invert" />
          </a>
          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <a
                key={l.testid}
                href={l.hash}
                onClick={scrollToHash(l.hash)}
                data-testid={l.testid}
                className="text-sm text-slate-300 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#contact"
            onClick={scrollToHash("#contact")}
            data-testid="navbar-cta-contact"
            className="hidden md:inline-flex bg-white text-slate-900 text-sm font-medium rounded-full px-5 py-2.5 hover:bg-slate-200 transition-colors duration-300"
          >
            Confier un dossier
          </a>
          <button
            onClick={() => toggle(true)}
            data-testid="mobile-menu-button"
            aria-label="Ouvrir le menu"
            className="md:hidden text-white p-2 -mr-2"
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-slate-900 flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-[72px] px-6 flex items-center justify-between border-b border-slate-800">
              <img src="/logo-acs-noir.webp" alt="ACS Corporate" className="h-8 w-auto brightness-0 invert" />
              <button
                onClick={() => toggle(false)}
                data-testid="mobile-menu-close"
                aria-label="Fermer le menu"
                className="text-white p-2 -mr-2"
              >
                <X className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.testid}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: EASE }}
                  href={l.hash}
                  onClick={go(l.hash)}
                  data-testid={`${l.testid}-mobile`}
                  className="font-sans font-extrabold text-white text-3xl tracking-tight hover:text-slate-300 transition-colors duration-300"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5, ease: EASE }}
                href="#contact"
                onClick={go("#contact")}
                data-testid="mobile-menu-cta"
                className="mt-6 inline-flex w-fit items-center bg-white text-slate-900 px-8 py-4 rounded-full font-medium"
              >
                Confier un dossier
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
